import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AiChat } from 'src/@types/ai';
import { AIInputStateControl } from 'src/features/AIInput';
import { useAIResponseStore } from 'src/sections/@dashboard/ai/Chat/AIConversation/AIMessages/useAIResponseState';
import { QueryKey } from 'src/utils/react-query';
import { useAIMessageQueries } from '../../queries/useAIMessageQueries';
import { useAIAudioPlayer } from '../AIMessages/AIMessageContent/AIMessageAudioPlayer/useAIAudioPlayer';
import { AIThinkMessageType, SendAIChatMessageProps } from './types';
import { useAIStreamController } from './useAIStreamController';

export function useAIMessageController(store: AIInputStateControl) {
  const client = useQueryClient();

  const { getAIResponse } = useAIMessageQueries(store);

  const { play, getAudioFromText } = useAIAudioPlayer(store);

  const { checkChunk } = useAIStreamController();

  const { audioAnswer, doSearch, think, setIsLoading, setAbortController } =
    store((state) => ({
      audioAnswer: state.audioAnswer,
      doSearch: state.doSearch,
      think: state.think,
      setIsLoading: state.setIsLoading,
      setAbortController: state.setAbortController,
      toggleSearch: state.toggleSearch,
    }));

  const { updateAIResponse, getAIMessage } = useAIResponseStore();

  const handleError = (errorMessage: string, throwError = true) => {
    updateAIResponse({
      error: errorMessage,
      isLoadingSources: false,
      isLoadingThinking: false,
    });
    if (!throwError) return;
    throw new Error(errorMessage);
  };

  const handleAIQuery = async (params: AIThinkMessageType) => {
    try {
      const streamingResponse = await getAIResponse(params);
      await handleAIStreamingResponse(streamingResponse);
      return getAIMessage();
    } catch (error) {
      handleError(
        error.message || 'Failed to generate report. Please try again.',
      );
    }
  };

  const handleAIStreamingResponse = async (response: Response) => {
    if (!response.ok || !response.body) {
      handleError('Failed to generate report. Please try again.');
      return;
    }

    const reader = response.body.getReader();
    if (!reader) {
      const errorMessage = 'No reader available';
      handleError(errorMessage);
      return;
    }

    updateAIResponse({ isLoadingThinking: false });

    const decoder = new TextDecoder();

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunks = decoder.decode(value).split('\n\n').filter(Boolean);

      for (const chunkSplit of chunks) {
        const chunk = checkChunk(chunkSplit);

        if (!chunk) continue;

        const isDone = chunk === '[DONE]';

        const aiMessage = getAIMessage();

        // if (!think) {
        const AIMessageContent = aiMessage?.content || '';

        const reasoningTime = aiMessage?.reasoningTime;

        if (chunk && reasoningTime?.start && !reasoningTime?.end) {
          const endTime = new Date().toISOString();
          updateAIResponse({
            reasoningTime: {
              start: reasoningTime.start,
              end: endTime,
            },
          });
        }

        const content = !isDone ? AIMessageContent + chunk : AIMessageContent;

        updateAIResponse({
          content,
          isLoadingThinking: false,
          isLoadingSources: false,
        });

        if (isDone) break;
      }
    }
  };

  const { mutateAsync } = useMutation({
    mutationFn: async ({
      userMessage,
      chatRef,
      contextMessage,
      messagesHistory,
      createConversation,
    }: SendAIChatMessageProps) => {
      try {
        const { content, contextFiles } = userMessage;
        const search = doSearch;
        const params = {
          think,
          search,
          content,
          chatRef,
          contextFiles,
          contextMessage,
          messagesHistory,
          createConversation,
        };

        if (think) {
          const startTime = new Date().toISOString();
          updateAIResponse({ reasoningTime: { start: startTime } });
        }

        // GET IA RESPONSE
        return await handleAIQuery(params);

        // GET NORMAL AI RESPONSE
        // if (!doSearch && !think) {
        //   returnMessage = await handleChatQuery(params);
        // } else {
        //   // GET SEARCH RESPONSE
        //   if (doSearch) {
        //     // returnMessage = await handleSearchQuery(params);
        //     returnMessage = await handleChatQuery({
        //       ...params,
        //       search: true,
        //     });
        //   } else {
        //     returnMessage = await handleThinkQuery(params);
        //   }
        // }
        // return returnMessage;
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred';
        updateAIResponse({
          error: errorMessage,
          isLoadingSources: false,
          isLoadingThinking: false,
        });
        return {
          ...getAIMessage(),
          error: errorMessage,
        };
      } finally {
        setIsLoading(false);
        setAbortController(null);
      }
    },
  });

  const sendAIChatMessage = async ({
    userMessage,
    chatRef,
    contextMessage,
    messagesHistory,
    createConversation,
  }: SendAIChatMessageProps) => {
    const finalMessage = await mutateAsync({
      userMessage,
      chatRef,
      contextMessage,
      messagesHistory,
      createConversation,
    });

    if (!finalMessage) {
      return;
    }

    const updatedMessage = {
      ...finalMessage,
      chat: chatRef,
      status: null,
    };

    updateAIResponse(updatedMessage);

    if (audioAnswer && finalMessage.content) {
      const messageId = finalMessage._id as string;
      const audio = await getAudioFromText(messageId, finalMessage.content);
      play(audio, messageId);
    }

    client.setQueryData<AiChat>([QueryKey.AI_CHATS, chatRef], (prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        messages: [...prev.messages, userMessage, updatedMessage] as any,
      };
    });
  };

  return {
    sendAIChatMessage,
  };
}
